/**
 * ProfileMigration + MigrationRun domain types.
 *
 * SOURCE OF TRUTH: `docs/decisions/business/ADR-102-profile-migration-engine.md`
 *   §2 business_profile_migrations
 *   §4 business_profile_migration_runs
 *   §5 canary gate strategy
 *
 * This frontend mirror is kept in parity via the (future) CI gate
 * `business-migration-schema-parity`.
 */
import { z } from 'zod';

export const BUMP_TYPES = ['patch', 'minor', 'major'] as const;
export type BumpType = (typeof BUMP_TYPES)[number];

export const MIGRATION_CLASSES = ['auto', 'hook', 'canary', 'manual'] as const;
export type MigrationClass = (typeof MIGRATION_CLASSES)[number];

export const MIGRATION_STATES = [
  'PENDING',
  'PRE_FLIGHT',
  'READY',
  'IN_PROGRESS',
  'COMPLETED',
  'FAILED',
  'ROLLED_BACK',
  'DEFERRED',
  'MANUAL_HOLD',
] as const;
export type MigrationState = (typeof MIGRATION_STATES)[number];

/**
 * The parent migration plan (one row per version-pair per sub-vertical).
 */
export interface ProfileMigrationRecord {
  id: string;
  subVerticalKey: string;
  subVerticalDisplayName: string;
  fromVersion: string;
  toVersion: string;
  bumpType: BumpType;
  migrationClass: MigrationClass;
  hookModule?: string;
  breakingChanges: Array<{ field: string; change: string }>;
  requiresDataMigration: boolean;
  rollbackPossible: boolean;
  createdAt: string;
  createdBy: string;
  startedAt?: string;
  /** Top-level state — computed from runs. */
  state: MigrationState;
  /** Canary gate currently serving (0 = not started, 5/25/75/100). */
  canaryPercentage: number;
  /** Next gate the operator can promote to. Undefined when at 100 or not canary. */
  nextGatePercentage?: number;
  /** Paused? Operators manually pause canary between gates. */
  paused: boolean;
  stats: MigrationStats;
}

export interface MigrationStats {
  eligible: number;
  migrated: number;
  deferred: number;
  failed: number;
  manualHold: number;
  pending: number;
}

/**
 * Per-tenant migration run.
 */
export interface MigrationRunRecord {
  id: string;
  migrationId: string;
  tenantId: string;
  tenantName: string;
  state: MigrationState;
  startedAt?: string;
  completedAt?: string;
  recordsMigrated?: number;
  errorMessage?: string;
  correlationId: string;
  attempts: number;
  lastAttemptAt?: string;
}

/**
 * Operator actions on a migration plan.
 */
export const MIGRATION_ACTIONS = [
  'pause',
  'resume',
  'proceed',
  'abort',
] as const;
export type MigrationAction = (typeof MIGRATION_ACTIONS)[number];

/**
 * Action dialog form contract — reason required (ECTP Rule 7).
 */
export const migrationActionFormSchema = z.object({
  action: z.enum(MIGRATION_ACTIONS),
  reason: z.string().min(10).max(500),
});
export type MigrationActionFormValues = z.infer<typeof migrationActionFormSchema>;

/**
 * Per-tenant inline actions (retry failed, rollback one).
 */
export const TENANT_RUN_ACTIONS = ['retry', 'rollback'] as const;
export type TenantRunAction = (typeof TENANT_RUN_ACTIONS)[number];

export const tenantRunActionFormSchema = z.object({
  action: z.enum(TENANT_RUN_ACTIONS),
  reason: z.string().min(10).max(500),
});
export type TenantRunActionFormValues = z.infer<typeof tenantRunActionFormSchema>;
